import React, { useState, useRef, useEffect } from 'react';
import { 
  Button, TextField, Grid, Box, Paper, Typography, CircularProgress, Alert, 
  Card, CardContent, CardActions, FormControl, InputLabel, Select, MenuItem, 
  Checkbox, ListItemText, OutlinedInput, Modal, Backdrop, Fade, Divider,
  RadioGroup, FormControlLabel, Radio, FormLabel, IconButton, Stepper, Step, 
  StepLabel, Avatar, List, ListItem, ListItemIcon, Dialog, DialogTitle, 
  DialogContent, DialogActions, Chip
} from '@mui/material';
import { 
  LocationOn, CameraAlt, QrCode, Memory, Close, Download, 
  Settings, Smartphone, Upload, CheckCircle, Code, Cable
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import ArduinoProgrammerUniversal from './ArduinoProgrammerUniversal';

const binTypes = ['General', 'Recyclable', 'Organic', 'Hazardous'];
const deploymentTypes = [
  { 
    value: 'qr', 
    label: 'QR Code Bin', 
    icon: <QrCode />, 
    description: 'Traditional bin with QR code for manual reporting',
    features: ['QR Code Generation', 'Manual Reporting', 'Photo Upload', 'Basic Tracking']
  },
  { 
    value: 'iot', 
    label: 'IoT Smart Bin', 
    icon: <Memory />, 
    description: 'Smart bin with sensors for real-time monitoring',
    features: ['Real-time Monitoring', 'Arduino Code Generation', 'Sensor Integration', 'Auto Alerts']
  }
];

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const AddBin = () => {
  const navigate = useNavigate();
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [location, setLocation] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [deploymentType, setDeploymentType] = useState('qr');
  const [generatedBinId, setGeneratedBinId] = useState('');
  const [arduinoCode, setArduinoCode] = useState('');
  const [showArduinoDialog, setShowArduinoDialog] = useState(false);
  const [showArduinoProgrammer, setShowArduinoProgrammer] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Choose Bin Type', 'Location & Zone', 'Specifications', 'Review & Deploy'];

  const [specs, setSpecs] = useState({
    capacity: '',
    wasteType: [],
    binHeight: '30' // Default height in cm
  });

  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/zones');
        console.log('Zones API Response:', response);
        
        const zonesData = response.data?.data || response.data || [];
        console.log('Parsed zones data:', zonesData);
        
        if (Array.isArray(zonesData)) {
          setZones(zonesData);
        } else {
          console.error('Invalid zones data format:', zonesData);
          setError('Failed to load zones. Invalid data format.');
        }
      } catch (err) {
        console.error('Error fetching zones:', err);
        setError('Failed to load zones. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchZones();
  }, []);

  const handleCameraOpen = () => {
    setCameraOpen(true);
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } 
    })
    .then(stream => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    })
    .catch(() => {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch(err => {
          console.error("Error accessing camera: ", err);
          setError('Could not access camera. Please ensure you have granted permission.');
          setCameraOpen(false);
        });
    });
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, videoRef.current.videoWidth, videoRef.current.videoHeight);
      
      canvasRef.current.toBlob(blob => {
        const capturedFile = new File([blob], `bin-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        setPhoto(capturedFile);
        setPhotoPreview(URL.createObjectURL(capturedFile));
        handleCameraClose();
      }, 'image/jpeg', 0.85);
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setLoading(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ 
          latitude: position.coords.latitude, 
          longitude: position.coords.longitude 
        });
        setLoading(false);
      },
      () => {
        setError('Unable to retrieve your location. Please ensure location services are enabled.');
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleSpecsChange = (field, value) => {
    setSpecs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWasteTypeChange = (event) => {
    const { value } = event.target;
    setSpecs(prev => ({
      ...prev,
      wasteType: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0: return !!deploymentType;
      case 1: return !!location && !!selectedZone;
      case 2: return (
        !!specs.capacity && 
        specs.wasteType.length > 0 && 
        (deploymentType === 'QR' || !!specs.binHeight)
      );
      case 3: return !!photo;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!isStepValid(activeStep)) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitLoading(true);
    setError('');

    try {
      const formData = new FormData();
      
      // Ensure deploymentType is lowercase to match backend validation
      const deploymentTypeLower = deploymentType.toLowerCase();
      formData.append('deploymentType', deploymentTypeLower);
      
      // Add location data
      if (location) {
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
      }
      
      // Get zone name from the selected zone ID
      const selectedZoneObj = zones.find(z => z._id === selectedZone);
      if (selectedZoneObj) {
        formData.append('zone', selectedZoneObj.name);
      } else {
        throw new Error('Please select a valid zone');
      }
      
      // Add bin specifications
      formData.append('capacity', specs.capacity);
      formData.append('wasteType', Array.isArray(specs.wasteType) ? specs.wasteType.join(',') : '');
      formData.append('binHeight', specs.binHeight || '30');
      
      // Add photo if available
      if (photo) {
        // If photo is a File object, append it directly
        if (photo instanceof File) {
          formData.append('photo', photo);
        } 
        // If it's a base64 string, convert to blob
        else if (typeof photo === 'string' && photo.startsWith('data:image')) {
          const blob = await fetch(photo).then(res => res.blob());
          formData.append('photo', blob, 'bin-photo.jpg');
        }
      }

      console.log('Submitting bin data:', {
        deploymentType: deploymentTypeLower,
        zone: selectedZoneObj.name,
        capacity: specs.capacity,
        wasteType: specs.wasteType,
        binHeight: specs.binHeight,
        hasPhoto: !!photo
      });

      // Submit the form data using the upload method
      const response = await apiClient.upload('/bins', formData);

      console.log('Bin created successfully:', response.data);

      // For IoT bins, show the Arduino programming dialog
      if (deploymentTypeLower === 'iot') {
        if (!response.data.binId || !response.data.arduinoCode) {
          throw new Error('Invalid response from server: Missing binId or arduinoCode');
        }
        setGeneratedBinId(response.data.binId);
        setArduinoCode(response.data.arduinoCode);
        setShowArduinoDialog(true);
      } else {
        // For QR bins, redirect to bins list
        navigate('/bins');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add bin. Please try again.');
      console.error('Error adding bin:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
        {deploymentType === 'iot' ? 'Add New IoT Smart Bin' : 'Add New QR Bin'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {activeStep === 0 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>Choose Bin Type</Typography>
            <RadioGroup
              value={deploymentType}
              onChange={(e) => setDeploymentType(e.target.value)}
              sx={{ mt: 2 }}
            >
              <Grid container spacing={2}>
                {deploymentTypes.map((type) => (
                  <Grid item xs={12} md={6} key={type.value}>
                    <Card 
                      variant="outlined"
                      sx={{
                        borderColor: deploymentType === type.value ? 'primary.main' : 'divider',
                        bgcolor: deploymentType === type.value ? 'primary.light' : 'background.paper',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          value={type.value}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  mr: 2,
                                  bgcolor: deploymentType === type.value ? 'primary.main' : 'grey.300'
                                }}
                              >
                                {type.icon}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">{type.label}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {type.description}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {type.features.map((feature, idx) => (
                                    <Chip 
                                      key={idx} 
                                      label={feature} 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: deploymentType === type.value ? 'primary.main' : 'grey.100',
                                        color: deploymentType === type.value ? '#fff' : 'inherit',
                                        fontSize: '0.7rem',
                                        height: 24
                                      }} 
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>Location & Zone</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Location</Typography>
                  {location ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Location captured: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Please capture the bin's current location
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleGetLocation}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <LocationOn />}
                  >
                    {loading ? 'Getting Location...' : 'Capture Current Location'}
                  </Button>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Zone</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Select Zone *</InputLabel>
                    <Select
                      value={selectedZone}
                      onChange={(e) => setSelectedZone(e.target.value)}
                      label="Select Zone *"
                      disabled={zones.length === 0 || loading}
                    >
                      {zones.length > 0 ? (
                        zones.map((zone) => (
                          <MenuItem key={zone._id} value={zone._id}>
                            {zone.name} ({zone.code})
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          {loading ? 'Loading zones...' : 'No zones available'}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>Bin Specifications</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={deploymentType === 'IoT' ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Capacity (liters) *"
                  type="number"
                  value={specs.capacity}
                  onChange={(e) => handleSpecsChange('capacity', e.target.value)}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              {deploymentType === 'iot' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bin Height (cm) *"
                    type="number"
                    value={specs.binHeight}
                    onChange={(e) => handleSpecsChange('binHeight', e.target.value)}
                    required
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Waste Type *</InputLabel>
                  <Select
                    multiple
                    value={specs.wasteType}
                    onChange={handleWasteTypeChange}
                    input={<OutlinedInput label="Waste Type *" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {binTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={specs.wasteType.indexOf(type) > -1} />
                        <ListItemText primary={type} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 3 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>Review & Add Photo</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Bin Details</Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Bin Type" 
                        secondary={deploymentType === 'IoT' ? 'IoT Smart Bin' : 'QR Code Bin'} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Location" 
                        secondary={location ? 
                          `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}` : 
                          'Not set'} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Zone" 
                        secondary={zones.find(z => z._id === selectedZone)?.name || 'Not selected'} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Capacity" 
                        secondary={`${specs.capacity} liters`} 
                      />
                    </ListItem>
                    {deploymentType === 'iot' && (
                      <>
                        <Divider />
                        <ListItem>
                          <ListItemText 
                            primary="Bin Height" 
                            secondary={`${specs.binHeight} cm`} 
                          />
                        </ListItem>
                      </>
                    )}
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Waste Type" 
                        secondary={specs.wasteType.join(', ')} 
                      />
                    </ListItem>
                  </List>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>Bin Photo *</Typography>
                  {photoPreview ? (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <img
                        src={photoPreview}
                        alt="Bin preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}
                      />
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        height: '200px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px dashed #ccc',
                        borderRadius: '4px',
                        mb: 2
                      }}
                    >
                      <Typography color="textSecondary">
                        No photo captured yet
                      </Typography>
                    </Box>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCameraOpen}
                    startIcon={<CameraAlt />}
                  >
                    {photoPreview ? 'Retake Photo' : 'Take Photo'}
                  </Button>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => {
            if (activeStep === 0) {
              navigate(-1);
            } else {
              setActiveStep(prev => prev - 1);
            }
          }}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isStepValid(activeStep) || submitLoading}
            startIcon={submitLoading ? <CircularProgress size={20} /> : null}
          >
            {submitLoading ? 'Adding Bin...' : 'Add Bin'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setActiveStep(prev => prev + 1)}
            disabled={!isStepValid(activeStep)}
          >
            Next
          </Button>
        )}
      </Box>

      <Dialog
        open={showArduinoDialog}
        onClose={() => setShowArduinoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Memory sx={{ mr: 1 }} />
            <span>IoT Bin Setup - {generatedBinId}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your IoT bin has been created successfully! Follow the steps below to complete the setup.
          </Alert>
          
          <Typography variant="h6" gutterBottom>Next Steps:</Typography>
          <List>
            <ListItem>
              <ListItemIcon><Download /></ListItemIcon>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Download Arduino Code</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => {
                      const element = document.createElement('a');
                      const file = new Blob([arduinoCode], { type: 'text/plain' });
                      element.href = URL.createObjectURL(file);
                      element.download = `binbuddy_${generatedBinId}.ino`;
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);
                    }}
                  >
                    Download
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Save this file to your computer. You'll need it to program the Arduino.
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                    {arduinoCode.split('\n').slice(0, 10).join('\n')}
                    {arduinoCode.split('\n').length > 10 ? '\n... (code truncated for preview)' : ''}
                  </pre>
                </Box>
              </Box>
            </ListItem>
            
            <Divider sx={{ my: 2 }} />
            
            <ListItem>
              <ListItemIcon><Cable /></ListItemIcon>
              <ListItemText 
                primary="Connect Arduino" 
                secondary="Use a USB cable to connect your Arduino to your computer"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon><Upload /></ListItemIcon>
              <ListItemText 
                primary="Upload Code" 
                secondary="Open the downloaded file in Arduino IDE and upload it to your board"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon><Settings /></ListItemIcon>
              <ListItemText 
                primary="Required Components" 
                secondary="HC-SR04 Ultrasonic Sensor, Arduino Board, Jumper Wires, Breadboard"
              />
            </ListItem>
          </List>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Important:</strong> Make sure to install the required libraries in Arduino IDE before uploading the code.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowArduinoDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowArduinoDialog(false);
              setShowArduinoProgrammer(true);
            }}
            startIcon={<Code />}
          >
            Open Programmer
          </Button>
        </DialogActions>
      </Dialog>

      <ArduinoProgrammerUniversal
        open={showArduinoProgrammer}
        onClose={async (programmed = false) => {
          try {
            if (programmed) {
              const formData = new FormData();
              formData.append('deploymentType', deploymentType);
              formData.append('binId', generatedBinId);
              formData.append('latitude', location.latitude);
              formData.append('longitude', location.longitude);
              formData.append('zone', selectedZone);
              formData.append('capacity', specs.capacity);
              formData.append('wasteType', specs.wasteType.join(','));
              formData.append('binHeight', specs.binHeight);
              formData.append('photo', photo);

              await apiClient.post('/bins', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });
              
              window.dispatchEvent(new Event('binsUpdated'));
              navigate('/bins');
            }
          } catch (err) {
            setError('Failed to save bin: ' + (err.response?.data?.message || err.message));
          } finally {
            setShowArduinoProgrammer(false);
          }
        }}
        binId={generatedBinId}
        arduinoCode={arduinoCode}
      />

      <Modal
        open={cameraOpen}
        onClose={handleCameraClose}
        aria-labelledby="camera-modal"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={cameraOpen}>
          <Box sx={style}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Take Photo</Typography>
              <IconButton onClick={handleCameraClose} size="small">
                <Close />
              </IconButton>
            </Box>
            
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              height: 0,
              paddingBottom: '75%',
              backgroundColor: '#000',
              mb: 2,
              overflow: 'hidden',
              borderRadius: 1
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCapture}
                startIcon={<CameraAlt />}
                size="large"
                sx={{ 
                  borderRadius: '50%', 
                  width: 64, 
                  height: 64,
                  minWidth: 64,
                  '& .MuiButton-startIcon': {
                    margin: 0
                  }
                }}
              />
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default AddBin;